import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/* 🔒 NOTIFY-OVER-DIALOG-2026-09-12 — ¿el evento salió del aviso global?
   Se detecta por los data-testid que SmartNotificationSystem ya trae, así que
   ese archivo no se toca. */
const NOTIFY_SEL =
  '[data-testid="notify-overlay"],[data-testid="notify-error-modal"],[data-testid="notify-confirm-modal"]';

function isFromNotify(e) {
  try {
    const t = e?.detail?.originalEvent?.target || e?.target;
    return !!(t && t.closest && t.closest(NOTIFY_SEL));
  } catch {
    return false;
  }
}

const DialogContent = React.forwardRef(({ className, children, hideCloseButton, overlayClassName, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay className={overlayClassName} />
    <DialogPrimitive.Content
      ref={ref}
      /* 🔒 NOTIFY-OVER-DIALOG-2026-09-12 — El aviso de error/confirmación de
         SmartNotificationSystem se monta con position:fixed y z-index 99999,
         o sea FUERA del arbol del diálogo. Radix veía ese clic como "clic
         afuera" y cerraba el formulario: si el cajero se equivocaba llenando
         una orden, al presionar "Entendido" perdía todo lo escrito.
         Acá se ignoran solo los clics que vienen del aviso. Cualquier otro
         clic afuera sigue cerrando el diálogo como siempre.
         Van ANTES de {...props} a propósito: CashRegister.js:1326 y
         PaymentScreen.js:3029 pasan los suyos y deben seguir mandando. */
      onPointerDownOutside={(e) => { if (isFromNotify(e)) e.preventDefault(); }}
      onInteractOutside={(e) => { if (isFromNotify(e)) e.preventDefault(); }}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}>
      {children}
      {!hideCloseButton && (
        <DialogPrimitive.Close
          className="absolute z-50 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none bg-muted hover:bg-muted/80 text-muted-foreground"
          style={{
            right: '12px',
            top: '12px',
            width: '32px',
            height: '32px',
            minWidth: '32px',
            minHeight: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            WebkitAppearance: 'none',
            appearance: 'none',
            border: 'none',
            cursor: 'pointer',
          }}>
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
